/** Applications list filter state + URL query sync (`/applications?...`). */

export type ApplicationsListFilters = {
  applicationIdContains: string;
  ccidContains: string;
  emailContains: string;
  phoneContains: string;
  status: string;
  productType: string;
  /**
   * Journey area the application is BLOCKED in.
   *
   * Formerly `stuckStep`, and formerly labelled "Journey" in the UI — which put it in direct
   * conflict with the JOURNEY column beside it. The column answers "how far did the customer
   * get" from completed milestones; this answers "where did it stop" from the last action and
   * its outcome. A row could therefore read `Journey: Not started` while matching a
   * `Journey: Identity` filter, which is exactly what QA saw. Both questions are worth
   * asking, so both kept their signal and only the naming was wrong.
   */
  blockedArea: string;
  /** Whether that block is a failure, an outstanding step, or a withdrawal. */
  blockedState: string;
  /** How far it got — the JOURNEY column's own value, so filter and column always agree. */
  journeyStage: string;
  /**
   * Show only applications a reviewer has manually corrected.
   *
   * Kept as a string rather than a boolean so it round-trips through the URL the
   * same way every other filter does, and an absent value cannot be confused with
   * an explicit "no".
   */
  editedOnly: string;
  createdFrom: string;
  createdTo: string;
  updatedFrom: string;
  updatedTo: string;
};

export const APPLICATIONS_LIST_FILTER_PARAM_KEYS = [
  'f',
  'aid',
  'ccid',
  'email',
  'phone',
  'status',
  'productType',
  // Read for saved links from before the rename; written as `blockedArea`.
  'stuckStep',
  'blockedArea',
  'blockedState',
  'journeyStage',
  'editedOnly',
  'openedFrom',
  'openedTo',
  'updatedFrom',
  'updatedTo',
  'page',
] as const;

/** Local calendar day as `YYYY-MM-DD` for HTML date inputs. */
export function todayYmd(d = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Expand a date-only filter to an inclusive local-day bound for the list API.
 * Plain `YYYY-MM-DD` parses as UTC midnight, so "Opened to = today" would exclude most of the day.
 */
export function ymdToApiBound(ymd: string, edge: 'start' | 'end'): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const dt =
    edge === 'start'
      ? new Date(y, m - 1, d, 0, 0, 0, 0)
      : new Date(y, m - 1, d, 23, 59, 59, 999);
  return dt.toISOString();
}

export function defaultApplicationsListFilters(now = new Date()): ApplicationsListFilters {
  const today = todayYmd(now);
  return {
    applicationIdContains: '',
    ccidContains: '',
    emailContains: '',
    phoneContains: '',
    status: '',
    productType: '',
    blockedArea: '',
    blockedState: '',
    journeyStage: '',
    editedOnly: '',
    createdFrom: today,
    createdTo: today,
    updatedFrom: '',
    updatedTo: '',
  };
}

function readParam(search: URLSearchParams, key: string): string {
  return (search.get(key) ?? '').trim();
}

function isYmd(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function readDateParam(search: URLSearchParams, key: string): string {
  const raw = readParam(search, key);
  return isYmd(raw) ? raw : '';
}

/** True when the URL carries any applications-list filter/page key. */
export function applicationsListSearchHasFilters(search: URLSearchParams): boolean {
  return APPLICATIONS_LIST_FILTER_PARAM_KEYS.some((key) => search.has(key));
}

export function parseApplicationsListFiltersFromSearch(search: URLSearchParams): {
  filters: ApplicationsListFilters;
  page: number;
  fromUrl: boolean;
} {
  const fromUrl = applicationsListSearchHasFilters(search);
  if (!fromUrl) {
    return { filters: defaultApplicationsListFilters(), page: 0, fromUrl: false };
  }

  const pageRaw = Number.parseInt(readParam(search, 'page'), 10);
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 0;

  return {
    fromUrl: true,
    page,
    filters: {
      applicationIdContains: readParam(search, 'aid'),
      ccidContains: readParam(search, 'ccid'),
      emailContains: readParam(search, 'email'),
      phoneContains: readParam(search, 'phone'),
      status: readParam(search, 'status'),
      productType: readParam(search, 'productType'),
      // `stuckStep` is the pre-rename key; a bookmarked or chat-shared URL still carries it.
      blockedArea: readParam(search, 'blockedArea') || readParam(search, 'stuckStep'),
      blockedState: readParam(search, 'blockedState'),
      journeyStage: readParam(search, 'journeyStage'),
      editedOnly: readParam(search, 'editedOnly'),
      createdFrom: readDateParam(search, 'openedFrom'),
      createdTo: readDateParam(search, 'openedTo'),
      updatedFrom: readDateParam(search, 'updatedFrom'),
      updatedTo: readDateParam(search, 'updatedTo'),
    },
  };
}

function setOrDelete(next: URLSearchParams, key: string, value: string): void {
  const v = value.trim();
  if (v) next.set(key, v);
  else next.delete(key);
}

/** Merge applied filters into a copy of the current search params (keeps unrelated keys). */
export function writeApplicationsListFiltersToSearch(
  prev: URLSearchParams,
  filters: ApplicationsListFilters,
  page: number,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  // Sentinel so “cleared dates / no chips” still counts as URL-owned state (hard refresh safe).
  next.set('f', '1');
  setOrDelete(next, 'aid', filters.applicationIdContains);
  setOrDelete(next, 'ccid', filters.ccidContains);
  setOrDelete(next, 'email', filters.emailContains);
  setOrDelete(next, 'phone', filters.phoneContains);
  // Always emit status (including empty) so chat-applied "any status" does not
  // look like a missing param and get filled by a default.
  next.set('status', filters.status.trim());
  setOrDelete(next, 'productType', filters.productType);
  setOrDelete(next, 'blockedArea', filters.blockedArea);
  setOrDelete(next, 'blockedState', filters.blockedState);
  setOrDelete(next, 'journeyStage', filters.journeyStage);
  setOrDelete(next, 'editedOnly', filters.editedOnly);
  // Retired key: clear it so an old value in the URL cannot outlive the filter it came from.
  next.delete('stuckStep');
  setOrDelete(next, 'openedFrom', filters.createdFrom);
  setOrDelete(next, 'openedTo', filters.createdTo);
  setOrDelete(next, 'updatedFrom', filters.updatedFrom);
  setOrDelete(next, 'updatedTo', filters.updatedTo);
  if (page > 0) next.set('page', String(page));
  else next.delete('page');
  return next;
}

const LIST_SEARCH_STORAGE_KEY = 'admin.applicationsListSearch';

/** In-memory fallback when `sessionStorage` is missing (SSR/tests) or blocked. */
let rememberedListSearchMemory = '';

/** React Router location.state key when opening a detail row from the list. */
export type ApplicationsListLocationState = {
  applicationsListSearch?: string;
};

/** Remember the list query so detail “Back” can restore filters after `/applications/:id`. */
export function rememberApplicationsListSearch(search: string | URLSearchParams): void {
  const q = typeof search === 'string' ? search.replace(/^\?/, '') : search.toString();
  rememberedListSearchMemory = q;
  try {
    if (typeof sessionStorage === 'undefined') return;
    if (q) sessionStorage.setItem(LIST_SEARCH_STORAGE_KEY, q);
    else sessionStorage.removeItem(LIST_SEARCH_STORAGE_KEY);
  } catch {
    /* private mode / blocked storage */
  }
}

export function readRememberedApplicationsListSearch(): string {
  try {
    if (typeof sessionStorage !== 'undefined') {
      const fromStore = sessionStorage.getItem(LIST_SEARCH_STORAGE_KEY);
      if (fromStore != null) return fromStore;
    }
  } catch {
    /* ignore */
  }
  return rememberedListSearchMemory;
}

/** Path back to the applications list, optionally with a query string (no leading `?` required). */
export function applicationsListPath(search?: string | null): string {
  const q = (search ?? '').replace(/^\?/, '').trim();
  return q ? `/applications?${q}` : '/applications';
}

/**
 * Prefer location.state from a list→detail navigation; fall back to last remembered list query.
 */
export function applicationsListPathFromNavState(state: unknown): string {
  const fromState = (state as ApplicationsListLocationState | null | undefined)?.applicationsListSearch;
  if (typeof fromState === 'string') {
    return applicationsListPath(fromState);
  }
  return rememberedApplicationsListPath();
}

/** Last remembered applications list path (query included), or bare `/applications`. */
export function rememberedApplicationsListPath(): string {
  return applicationsListPath(readRememberedApplicationsListSearch());
}

/**
 * Query string to re-apply when the URL has no list-filter keys (nav / redirects to bare `/applications`).
 * Returns null when nothing usable was remembered (caller should seed defaults).
 */
export function applicationsListSearchToRestoreWhenEmpty(): string | null {
  const remembered = readRememberedApplicationsListSearch().replace(/^\?/, '').trim();
  if (!remembered) return null;
  const params = new URLSearchParams(remembered);
  return applicationsListSearchHasFilters(params) ? remembered : null;
}
